import { useState, useEffect, useCallback } from 'react';
import { listConferenceRooms, getConferenceRoomAvailability, getOfficeLocations } from 'services/Settings';
import { setTimezoneWithoutAlteringTime } from 'utils/utility_functions/timezone';
import { useUserDetailsContext } from 'context/userDetailsContext';

export const useConferenceRoomAvailability = (selectedLocation, meetingStartTime, meetingEndTime, options = {}, excludeEventId = null) => {
  const [conferenceRooms, setConferenceRooms] = useState([]);
  const [roomAvailability, setRoomAvailability] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get user context for timezone
  const { userDetails } = useUserDetailsContext();

  // Options to control behavior
  const {
    autoCheckAvailability = false, // Only check availability automatically if explicitly enabled
    checkOnTimeChange = false, // Only check when time changes if explicitly enabled
  } = options;

  // Fetch conference rooms for the selected location
  const fetchConferenceRooms = useCallback(async () => {
    if (!selectedLocation) {
      setConferenceRooms([]);
      setRoomAvailability({});
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch both conference rooms and office locations
      const [roomsResponse, locationsResponse] = await Promise.all([listConferenceRooms(selectedLocation), getOfficeLocations()]);

      const rooms = roomsResponse?.data || [];
      const locations = locationsResponse?.data || [];

      // Create office name map
      const officeNameMap = {};
      locations.forEach((loc) => {
        const locationId = loc?.office_location_id || loc?.sk;
        officeNameMap[locationId] = loc?.office_name;
      });

      // Filter rooms for the selected location and add office names
      const filteredRooms = rooms
        .filter((room) => String(room?.office_location_id) === String(selectedLocation))
        .map((room) => ({
          ...room,
          office_name: officeNameMap[room?.office_location_id] || 'Unknown Office',
          label: room?.room_name || room?.name,
          value: room?.conference_room_id || room?.room_id || room?.id,
        }));

      setConferenceRooms(filteredRooms);
    } catch (err) {
      console.error('Error fetching conference rooms:', err);
      setError('Failed to load conference rooms');
      setConferenceRooms([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocation]);

  // Check availability for all rooms
  const checkRoomAvailability = useCallback(async () => {
    if (!selectedLocation || !meetingStartTime || !meetingEndTime || conferenceRooms.length === 0) {
      setRoomAvailability({});
      return;
    }

    // Note: Time validation is now handled in the UI components
    // The end time will be automatically moved to the next day if it's before start time

    try {
      const conferenceRoomIds = conferenceRooms.map((room) => room.value);

      // Format times using the same timezone conversion as create/update event APIs
      const formattedStartTime = setTimezoneWithoutAlteringTime(new Date(meetingStartTime), userDetails?.timezone);
      const formattedEndTime = setTimezoneWithoutAlteringTime(new Date(meetingEndTime), userDetails?.timezone);

      const availabilityData = {
        office_location_id: selectedLocation,
        meeting_start_time: formattedStartTime,
        meeting_end_time: formattedEndTime,
        conference_room_list: conferenceRoomIds,
      };

      // Add exclude_event_id if provided (for edit mode)
      if (excludeEventId) {
        availabilityData.exclude_event_id = excludeEventId;
      }

      const availabilityResponse = await getConferenceRoomAvailability(availabilityData);
      const availabilityResults = availabilityResponse?.data || {};

      // Create availability map from the response object
      const availabilityMap = {};
      conferenceRoomIds.forEach((roomId) => {
        // Check if the room exists in response and get its availability status
        const roomAvailability = availabilityResults[roomId];
        availabilityMap[roomId] = roomAvailability?.available === true;
      });

      setRoomAvailability(availabilityMap);
    } catch (err) {
      console.error('Error checking room availability:', err);
      // Set all rooms as unavailable if availability check fails
      // This prevents users from selecting potentially booked rooms
      const fallbackAvailability = {};
      conferenceRooms.forEach((room) => {
        fallbackAvailability[room.value] = false;
      });
      setRoomAvailability(fallbackAvailability);
    }
  }, [selectedLocation, meetingStartTime, meetingEndTime, conferenceRooms, userDetails?.timezone, excludeEventId]);

  // Fetch rooms when location changes
  useEffect(() => {
    fetchConferenceRooms();
  }, [fetchConferenceRooms]);

  // Check availability when rooms are loaded (only if auto-check is enabled)
  useEffect(() => {
    if (conferenceRooms.length > 0 && autoCheckAvailability) {
      checkRoomAvailability();
    }
  }, [checkRoomAvailability, conferenceRooms.length, autoCheckAvailability]);

  // Check availability when time changes (only if explicitly enabled)
  useEffect(() => {
    if (checkOnTimeChange && conferenceRooms.length > 0 && meetingStartTime && meetingEndTime) {
      checkRoomAvailability();
    }
  }, [checkOnTimeChange, meetingStartTime, meetingEndTime, checkRoomAvailability, conferenceRooms.length]);

  // Generate room options with availability status
  const roomOptionsWithAvailability = conferenceRooms.map((room) => {
    const isAvailable = roomAvailability[room.value];
    const hasAvailabilityData = roomAvailability.hasOwnProperty(room.value);

    const capacityText = room.capacity ? ` (${room.capacity})` : '';

    // Show availability status in dropdown
    let availabilityText = '';
    if (hasAvailabilityData) {
      availabilityText = isAvailable ? ' (Available)' : ' (Unavailable)';
    } else {
      availabilityText = ' (Checking...)';
    }

    return {
      ...room,
      label: `${room.label}${capacityText}`, // Clean label for selected value display
      dropdownLabel: `${room.label}${capacityText}${availabilityText}`, // Full label with availability for dropdown
      isAvailable: isAvailable,
      disabled: hasAvailabilityData && isAvailable === false, // Disable unavailable rooms
      hasAvailabilityData: hasAvailabilityData,
    };
  });

  return {
    conferenceRooms: roomOptionsWithAvailability,
    isLoading,
    error,
    refreshAvailability: checkRoomAvailability,
  };
};
